export type VideoProvider = 'youtube' | 'vimeo';

export interface VideoEmbedOptions {
  autoplay: boolean;
  loop: boolean;
  hideControls: boolean;
  /** YouTube: reduzir marca/título do player */
  modestBranding: boolean;
  /** YouTube: não sugerir vídeos de outros canais */
  noRelated: boolean;
  /** YouTube: iniciar em (segundos) */
  start: number;
  /** Vimeo: ocultar título */
  hideTitle: boolean;
  /** Vimeo: ocultar nome do autor */
  hideByline: boolean;
  /** Vimeo: ocultar foto do autor */
  hidePortrait: boolean;
}

export const defaultVideoOptions: VideoEmbedOptions = {
  autoplay: false,
  loop: false,
  hideControls: false,
  modestBranding: true,
  noRelated: true,
  start: 0,
  hideTitle: true,
  hideByline: true,
  hidePortrait: true,
};

export interface ParsedVideo {
  provider: VideoProvider;
  id: string;
  options: VideoEmbedOptions;
}

const YT_PATTERNS = [
  /youtube\.com\/watch\?[^#]*v=([\w-]{6,})/i,
  /youtu\.be\/([\w-]{6,})/i,
  /youtube\.com\/embed\/([\w-]{6,})/i,
  /youtube-nocookie\.com\/embed\/([\w-]{6,})/i,
  /youtube\.com\/shorts\/([\w-]{6,})/i,
  /youtube\.com\/live\/([\w-]{6,})/i,
];

const VIMEO_PATTERNS = [
  /player\.vimeo\.com\/video\/(\d+)/i,
  /vimeo\.com\/(?:channels\/[\w-]+\/)?(\d+)/i,
];

const boolParam = (params: URLSearchParams, key: string, fallback: boolean) => {
  const raw = params.get(key);
  if (raw === null) return fallback;
  return raw === '1' || raw === 'true';
};

/**
 * Reconhece links do YouTube e do Vimeo (inclusive links de player já salvos,
 * relendo os parâmetros de exibição).
 */
export const parseVideoUrl = (url: string): ParsedVideo | null => {
  const trimmed = (url || '').trim();
  if (!trimmed) return null;

  let params = new URLSearchParams();
  try {
    params = new URL(trimmed).searchParams;
  } catch {
    /* link sem protocolo: segue sem parâmetros */
  }

  for (const re of YT_PATTERNS) {
    const m = trimmed.match(re);
    if (m) {
      const startRaw = params.get('start') || params.get('t') || '';
      return {
        provider: 'youtube',
        id: m[1],
        options: {
          ...defaultVideoOptions,
          autoplay: boolParam(params, 'autoplay', false),
          loop: boolParam(params, 'loop', false),
          hideControls: params.get('controls') === '0',
          modestBranding: params.has('modestbranding')
            ? params.get('modestbranding') === '1'
            : defaultVideoOptions.modestBranding,
          noRelated: params.has('rel') ? params.get('rel') === '0' : defaultVideoOptions.noRelated,
          start: Number(startRaw.replace(/\D/g, '')) || 0,
        },
      };
    }
  }

  for (const re of VIMEO_PATTERNS) {
    const m = trimmed.match(re);
    if (m) {
      return {
        provider: 'vimeo',
        id: m[1],
        options: {
          ...defaultVideoOptions,
          autoplay: boolParam(params, 'autoplay', false),
          loop: boolParam(params, 'loop', false),
          hideControls: params.get('controls') === '0',
          hideTitle: params.has('title') ? params.get('title') === '0' : defaultVideoOptions.hideTitle,
          hideByline: params.has('byline') ? params.get('byline') === '0' : defaultVideoOptions.hideByline,
          hidePortrait: params.has('portrait')
            ? params.get('portrait') === '0'
            : defaultVideoOptions.hidePortrait,
        },
      };
    }
  }

  return null;
};

export const buildEmbedUrl = (
  provider: VideoProvider,
  id: string,
  options: VideoEmbedOptions,
): string => {
  if (!id) return '';

  if (provider === 'youtube') {
    const p = new URLSearchParams();
    if (options.autoplay) {
      p.set('autoplay', '1');
      p.set('mute', '1');
    }
    if (options.loop) {
      p.set('loop', '1');
      p.set('playlist', id);
    }
    if (options.hideControls) p.set('controls', '0');
    if (options.modestBranding) p.set('modestbranding', '1');
    p.set('rel', options.noRelated ? '0' : '1');
    if (options.start > 0) p.set('start', String(Math.floor(options.start)));
    const qs = p.toString();
    return `https://www.youtube-nocookie.com/embed/${id}${qs ? `?${qs}` : ''}`;
  }

  const p = new URLSearchParams();
  if (options.autoplay) {
    p.set('autoplay', '1');
    p.set('muted', '1');
  }
  if (options.loop) p.set('loop', '1');
  if (options.hideControls) p.set('controls', '0');
  if (options.hideTitle) p.set('title', '0');
  if (options.hideByline) p.set('byline', '0');
  if (options.hidePortrait) p.set('portrait', '0');
  const qs = p.toString();
  return `https://player.vimeo.com/video/${id}${qs ? `?${qs}` : ''}`;
};

export const VIDEO_PROVIDER_LABELS: Record<VideoProvider, string> = {
  youtube: 'YouTube',
  vimeo: 'Vimeo',
};

export const VIDEO_IFRAME_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture';

const thumbCache = new Map<string, string | null>();

/**
 * Capa pública do vídeo (YouTube/Vimeo), usada como fundo desfocado.
 * Resultado fica em cache para evitar buscas repetidas.
 */
export const getVideoThumbnail = async (url: string): Promise<string | null> => {
  const key = (url || '').trim();
  if (!key) return null;
  if (thumbCache.has(key)) return thumbCache.get(key) ?? null;

  const parsed = parseVideoUrl(key);
  if (!parsed) {
    thumbCache.set(key, null);
    return null;
  }

  if (parsed.provider === 'youtube') {
    const thumb = `https://i.ytimg.com/vi/${parsed.id}/maxresdefault.jpg`;
    thumbCache.set(key, thumb);
    return thumb;
  }

  try {
    const res = await fetch(
      `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${parsed.id}`)}`,
    );
    if (!res.ok) throw new Error('oembed indisponível');
    const data = (await res.json()) as { thumbnail_url?: string };
    const thumb = data?.thumbnail_url || null;
    thumbCache.set(key, thumb);
    return thumb;
  } catch {
    thumbCache.set(key, null);
    return null;
  }
};

/** Fallback do YouTube quando a capa em alta resolução não existe. */
export const getYoutubeFallbackThumbnail = (url: string): string | null => {
  const parsed = parseVideoUrl(url);
  if (!parsed || parsed.provider !== 'youtube') return null;
  return `https://i.ytimg.com/vi/${parsed.id}/hqdefault.jpg`;
};
